import { Clock, Phone, LayoutDashboard, MessageCircle, Building2 } from "lucide-react";

/**
 * AccessModelCallout — a small, reusable band that communicates the CCCHAPS
 * access model consistently across public pages:
 *   • 24/7 on-call chaplain access
 *   • scheduled onsite or remote visits
 *   • CALLACHAP (when operationally accurate for the page)
 *   • other digital support tools included in the organization's care arrangement
 *
 * IMPORTANT ACCURACY:
 *   • The 24/7 promise applies to on-call chaplain access — not continuous
 *     onsite presence.
 *   • Scheduled onsite visits are described as scheduled/recurring.
 *   • CALLACHAP communication features are not described as live.
 *
 * Props:
 *   variant           — tailors the copy naturally to a page context.
 *                       Supported: "default" (also for workplace / crisis /
 *                       for-organizations / packages), "healthcare",
 *                       "senior-living", "leadership",
 *                       "industry-automotive", "industry-restaurants",
 *                       "industry-construction".
 *   showCallachap     — include CALLACHAP in the paragraph (default: true).
 *   supportingPhrase  — render the "Multiple ways to connect…" tagline
 *                       (default: true).
 *   testId            — override data-testid for scoped verification.
 *   className         — additional wrapper classes.
 */

const VARIANTS = {
  default: {
    body: (showCallachap) =>
      showCallachap
        ? "Chaplain support is always within reach. Employees and their families can access 24/7 on-call chaplain support, connect through scheduled onsite or remote visits, and use CALLACHAP and other digital support options included in their organization's care arrangement."
        : "Chaplain support is always within reach. Employees and their families can access 24/7 on-call chaplain support, connect through scheduled onsite or remote visits, and use the digital support options included in their organization's care arrangement.",
  },
  healthcare: {
    body: (showCallachap) =>
      showCallachap
        ? "Chaplain support stays available to healthcare teams and families beyond scheduled rounds or visits. Employees and their immediate household members can reach a chaplain 24/7 on-call, connect through scheduled onsite or remote visits, and use CALLACHAP and other digital support options included in their organization's care arrangement."
        : "Chaplain support stays available to healthcare teams and families beyond scheduled rounds or visits. Employees and their immediate household members can reach a chaplain 24/7 on-call, connect through scheduled onsite or remote visits, and use the digital support options included in their organization's care arrangement.",
  },
  "senior-living": {
    body: (showCallachap) =>
      showCallachap
        ? "Scheduled onsite chaplain presence in senior living and board & care communities is complemented by 24/7 on-call chaplain access for eligible employees and their immediate household members — with remote visits, CALLACHAP, and other digital support options included in the organization's care arrangement."
        : "Scheduled onsite chaplain presence in senior living and board & care communities is complemented by 24/7 on-call chaplain access for eligible employees and their immediate household members — with remote visits and the digital support options included in the organization's care arrangement.",
  },
  leadership: {
    body: (showCallachap) =>
      showCallachap
        ? "Confidential leadership and employee support is always within reach. Leaders and employees can access 24/7 on-call chaplain support, meet through scheduled onsite or remote conversations, and use CALLACHAP and other digital support options included in the organization's care arrangement."
        : "Confidential leadership and employee support is always within reach. Leaders and employees can access 24/7 on-call chaplain support, meet through scheduled onsite or remote conversations, and use the digital support options included in the organization's care arrangement.",
  },
  "industry-automotive": {
    body: (showCallachap) =>
      showCallachap
        ? "Support does not end when the scheduled dealership visit ends. Employees and their families can reach a chaplain 24/7 on-call, connect through scheduled onsite or remote visits, and use CALLACHAP and other digital support options included in the organization's care arrangement — across sales, service, technicians, parts, and back-office roles."
        : "Support does not end when the scheduled dealership visit ends. Employees and their families can reach a chaplain 24/7 on-call, connect through scheduled onsite or remote visits, and use the digital support options included in the organization's care arrangement — across sales, service, technicians, parts, and back-office roles.",
  },
  "industry-restaurants": {
    body: (showCallachap) =>
      showCallachap
        ? "Restaurant, quick-service, and hospitality teams work varied shifts — evenings, weekends, and nontraditional schedules. Employees and their families can reach a chaplain 24/7 on-call, connect through scheduled onsite or remote visits, and use CALLACHAP and other digital support options included in the organization's care arrangement."
        : "Restaurant, quick-service, and hospitality teams work varied shifts — evenings, weekends, and nontraditional schedules. Employees and their families can reach a chaplain 24/7 on-call, connect through scheduled onsite or remote visits, and use the digital support options included in the organization's care arrangement.",
  },
  "industry-construction": {
    body: (showCallachap) =>
      showCallachap
        ? "Support is not limited to the chaplain's scheduled jobsite presence. Field crews, skilled trades, supervisors, and office staff can reach a chaplain 24/7 on-call, connect through scheduled onsite or remote visits, and use CALLACHAP and other digital support options included in the organization's care arrangement."
        : "Support is not limited to the chaplain's scheduled jobsite presence. Field crews, skilled trades, supervisors, and office staff can reach a chaplain 24/7 on-call, connect through scheduled onsite or remote visits, and use the digital support options included in the organization's care arrangement.",
  },
  "industry-real-estate": {
    body: (showCallachap) =>
      showCallachap
        ? "Real estate professionals rarely work a traditional 9-to-5, and CCCHAPS support is designed to stay accessible beyond scheduled office visits. Agents, brokers, brokerage office teams, and title & escrow professionals can reach a chaplain 24/7 on-call, connect through scheduled onsite or remote visits, and use CALLACHAP and other digital support options included in the organization's care arrangement."
        : "Real estate professionals rarely work a traditional 9-to-5, and CCCHAPS support is designed to stay accessible beyond scheduled office visits. Agents, brokers, brokerage office teams, and title & escrow professionals can reach a chaplain 24/7 on-call, connect through scheduled onsite or remote visits, and use the digital support options included in the organization's care arrangement.",
  },
};

const CHIPS_BASE = [
  { icon: Clock, label: "24/7 on-call chaplain access" },
  { icon: Building2, label: "Scheduled onsite or remote visits" },
  { icon: LayoutDashboard, label: "CCCHAPS Care Portal" },
];

export default function AccessModelCallout({
  variant = "default",
  showCallachap = true,
  supportingPhrase = true,
  testId = "access-model-callout",
  className = "",
}) {
  const cfg = VARIANTS[variant] || VARIANTS.default;
  const body = cfg.body(showCallachap);
  const chips = showCallachap
    ? [...CHIPS_BASE, { icon: Phone, label: "CALLACHAP where included" }]
    : [...CHIPS_BASE, { icon: MessageCircle, label: "Remote support" }];

  return (
    <aside
      data-testid={testId}
      className={`rounded-2xl border border-blue-100 bg-[#F0F4F8] p-6 md:p-8 ${className}`}
    >
      <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
        Available 24/7
      </p>
      <p className="text-base sm:text-lg text-slate-800 leading-relaxed max-w-3xl">
        {body}
      </p>

      <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
        {chips.map((c) => {
          const Icon = c.icon;
          return (
            <li
              key={c.label}
              className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5"
            >
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
                <Icon className="w-4 h-4" strokeWidth={1.75} />
              </span>
              <span className="text-xs font-medium text-slate-800 leading-tight">
                {c.label}
              </span>
            </li>
          );
        })}
      </ul>

      {supportingPhrase && (
        <p className="mt-4 text-sm text-[#3B597B] font-medium">
          Multiple ways to connect. One consistent source of support.
        </p>
      )}

      <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
        24/7 refers to on-call chaplain availability. Scheduled onsite presence is recurring and based on the organization's care arrangement. Response times vary by channel.
      </p>
    </aside>
  );
}
